import { useId } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export type FilterKey =
  | 'categoryId'
  | 'accountId'
  | 'isTransfer'
  | 'isExcluded'
  | 'isHidden'
  | 'needsReview';
export type FilterValues = Partial<Record<FilterKey, string | undefined>>;

type Option = { value: string; label: string };
type OptionGroup = { label: string; options: Option[] };

const ALL = '__all__';

export const YES_NO_LABELS: Record<
  'isTransfer' | 'isExcluded' | 'needsReview',
  { true: string; false: string }
> = {
  isTransfer: { true: 'Endast överföringar', false: 'Utan överföringar' },
  isExcluded: { true: 'Endast exkluderade', false: 'Utan exkluderade' },
  needsReview: { true: 'Behöver granskas', false: 'Granskade' },
};

/**
 * Hidden rows are left out unless asked for, so "no filter" means without
 * them: the options are "also hidden" and "only hidden".
 */
export const HIDDEN_LABELS: Record<'all' | 'true', string> = {
  all: 'Visa även dolda',
  true: 'Endast dolda',
};

const yesNo = (key: keyof typeof YES_NO_LABELS): Option[] => [
  { value: 'true', label: YES_NO_LABELS[key].true },
  { value: 'false', label: YES_NO_LABELS[key].false },
];

/** All list filters in one sheet (bottom sheet on phones). Changes apply right away. */
export function FilterSheet({
  open,
  onOpenChange,
  values,
  onChange,
  onClear,
  categoryGroups,
  accounts,
  total,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  values: FilterValues;
  onChange: (key: FilterKey, value: string | undefined) => void;
  onClear: () => void;
  categoryGroups: OptionGroup[];
  accounts: Option[];
  total: number | undefined;
}) {
  const active = Object.values(values).some(Boolean);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="pr-10">
          <DialogTitle className="text-[15px] font-semibold">Filter</DialogTitle>
          <DialogDescription className="sr-only">
            Begränsa listan till vissa kategorier, konton eller typer av köp.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 gap-4">
          <FilterSelect
            label="Kategori"
            allLabel="Alla kategorier"
            value={values.categoryId}
            onValueChange={(v) => onChange('categoryId', v)}
            groups={categoryGroups}
          />
          <FilterSelect
            label="Konto"
            allLabel="Alla konton"
            value={values.accountId}
            onValueChange={(v) => onChange('accountId', v)}
            groups={[{ label: '', options: accounts }]}
          />
          <FilterSelect
            label="Överföringar"
            allLabel="Med överföringar"
            value={values.isTransfer}
            onValueChange={(v) => onChange('isTransfer', v)}
            groups={[{ label: '', options: yesNo('isTransfer') }]}
          />
          <FilterSelect
            label="Exkluderade"
            allLabel="Med exkluderade"
            value={values.isExcluded}
            onValueChange={(v) => onChange('isExcluded', v)}
            groups={[{ label: '', options: yesNo('isExcluded') }]}
          />
          <FilterSelect
            label="Dolda"
            allLabel="Utan dolda"
            value={values.isHidden}
            onValueChange={(v) => onChange('isHidden', v)}
            groups={[
              {
                label: '',
                options: [
                  { value: 'all', label: HIDDEN_LABELS.all },
                  { value: 'true', label: HIDDEN_LABELS.true },
                ],
              },
            ]}
          />
          <FilterSelect
            label="Granskning"
            allLabel="Alla"
            value={values.needsReview}
            onValueChange={(v) => onChange('needsReview', v)}
            groups={[{ label: '', options: yesNo('needsReview') }]}
          />
        </div>

        <DialogFooter>
          {active && (
            <Button type="button" variant="ghost" onClick={onClear}>
              Rensa filter
            </Button>
          )}
          <Button type="button" onClick={() => onOpenChange(false)}>
            {total == null ? 'Visa' : `Visa ${total} köp`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FilterSelect({
  label,
  allLabel,
  value,
  onValueChange,
  groups,
}: {
  label: string;
  allLabel: string;
  value: string | undefined;
  onValueChange: (v: string | undefined) => void;
  groups: OptionGroup[];
}) {
  const id = useId();
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <Label htmlFor={id} className="text-xs text-muted-foreground">
        {label}
      </Label>
      <Select value={value || ALL} onValueChange={(v) => onValueChange(v === ALL ? undefined : v)}>
        <SelectTrigger id={id} className="w-full">
          <SelectValue placeholder={allLabel} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>{allLabel}</SelectItem>
          {groups.map((g, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: groups are static per render and may lack a label
            <SelectGroup key={`${i}-${g.label}`}>
              {g.label && <SelectLabel>{g.label}</SelectLabel>}
              {g.options.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectGroup>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
